import dagre from '@dagrejs/dagre';
import { Node, Edge, Position } from 'reactflow';

// Define layout options type
export interface LayoutOptions {
  direction: 'TB' | 'LR' | 'RL' | 'BT';
  spacing: [number, number]; // e.g., [nodesep, ranksep]
}

// Initialize the Dagre graph instance
const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

/**
 * Calculates layout using Dagre.
 * @param nodes - Array of React Flow nodes.
 * @param edges - Array of React Flow edges.
 * @param options - Layout options (direction, spacing).
 * @returns A promise resolving to the layouted nodes.
 */
export const getLayoutedElements = async (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions
): Promise<Node[]> => {
  // Configure Dagre graph settings
  dagreGraph.setGraph({
    rankdir: options.direction,
    nodesep: options.spacing[0],
    ranksep: options.spacing[1],
    // Add other Dagre options if needed (e.g., align, marginx, marginy)
  });

  // Clear previous graph elements (important for re-layout)
  dagreGraph.nodes().forEach((nodeId) => dagreGraph.removeNode(nodeId));
  // Correctly remove edges using source (v) and target (w) node IDs
  dagreGraph.edges().forEach((edge) => dagreGraph.removeEdge(edge.v, edge.w));

  // Add nodes to Dagre graph
  nodes.forEach((node) => {
    // Use measured dimensions if available, otherwise estimate or use defaults
    // NOTE: Ensure nodes have dimensions before calling this for accurate layout.
    // React Flow usually calculates these after the first render.
    // Use node.width/height if available, otherwise use defaults.
    const nodeWidth = node.width || 150; // Default width if not set
    const nodeHeight = node.height || 50; // Default height if not set

    dagreGraph.setNode(node.id, {
      width: nodeWidth,
      height: nodeHeight,
    });
  });

  // Add edges to Dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run Dagre layout algorithm
  // Note: @types/dagre might expect more arguments, but usage with just the graph seems common.
  // Casting the method to 'any' to bypass the persistent type error.
  await (dagre.layout as any)(dagreGraph);

  // Apply calculated positions to React Flow nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (!nodeWithPosition) {
        console.warn(`Dagre node not found for ID: ${node.id}`);
        return node; // Return original node if layout failed for it
    }

    // Use the same width/height logic as when setting the node
    const nodeWidth = node.width || 150;
    const nodeHeight = node.height || 50;

    // We are shifting the dagre node position (anchor=center) to the top left
    // so it matches the React Flow node anchor point (top left).
    const position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return {
      ...node,
      position,
      // Optionally update handle positions based on direction
      sourcePosition: options.direction === 'LR' ? Position.Right : Position.Bottom,
      targetPosition: options.direction === 'LR' ? Position.Left : Position.Top,
      style: { ...node.style, opacity: 1 }, // Ensure node is visible
    };
  });

  return layoutedNodes;
};
